import {
	BotIcon,
	CalendarDaysIcon,
	CalendarRangeIcon,
	ChartLineIcon,
	CompassIcon,
	CreditCardIcon,
	FolderKanbanIcon,
	ImagesIcon,
	InboxIcon,
	LayoutDashboardIcon,
	LayoutGridIcon,
	type LucideIcon,
	MegaphoneIcon,
	RadarIcon,
	SettingsIcon,
	SlidersHorizontalIcon,
	SparklesIcon,
	WorkflowIcon,
} from "lucide-react";
import type { PreloadablePage } from "./lazy-page";
import { pages } from "./pages";

export type NavItem = {
	id: string;
	label: string;
	icon: LucideIcon;
	/** Two-key "go to" sequence, e.g. `g f`. */
	shortcut: string;
	page: PreloadablePage;
};

export type ProjectNavSection = "monitor" | "plan" | "automate";

export type ProjectNavItem = NavItem & {
	segment: string;
	section?: ProjectNavSection;
	/** Reachable from the command palette and shortcuts, but not listed in the sidebar. */
	hidden?: boolean;
};
export type WorkspaceNavItem = NavItem & { path: string };

export const projectPath = (projectId: string, segment = "") =>
	`/p/${projectId}${segment ? `/${segment}` : ""}`;

export const projectNavSections: { id: ProjectNavSection; label: string }[] = [
	{ id: "monitor", label: "Monitor" },
	{ id: "plan", label: "Plan" },
	{ id: "automate", label: "Automate" },
];

export const projectNavigation: ProjectNavItem[] = [
	{
		id: "overview",
		label: "Overview",
		segment: "overview",
		section: "monitor",
		icon: LayoutDashboardIcon,
		shortcut: "g h",
		page: pages.overview,
	},
	{
		id: "content",
		label: "Content",
		segment: "content",
		section: "monitor",
		icon: LayoutGridIcon,
		shortcut: "g p",
		page: pages.content,
	},
	{
		id: "inbox",
		label: "Inbox",
		segment: "inbox",
		section: "monitor",
		icon: InboxIcon,
		shortcut: "g i",
		page: pages.inbox,
	},
	{
		id: "analytics",
		label: "Analytics",
		segment: "analytics",
		section: "monitor",
		icon: ChartLineIcon,
		shortcut: "g n",
		page: pages.analytics,
	},
	{
		id: "strategy",
		label: "Strategy",
		segment: "strategy",
		section: "plan",
		icon: CompassIcon,
		shortcut: "g s",
		page: pages.strategy,
	},
	{
		id: "plan",
		label: "Plan",
		segment: "plan",
		section: "plan",
		icon: CalendarRangeIcon,
		shortcut: "g l",
		page: pages.plan,
	},
	{
		id: "calendar",
		label: "Calendar",
		segment: "calendar",
		section: "plan",
		icon: CalendarDaysIcon,
		shortcut: "g c",
		page: pages.calendar,
	},
	{
		id: "competitors",
		label: "Competitors",
		segment: "competitors",
		section: "plan",
		icon: RadarIcon,
		shortcut: "g m",
		page: pages.competitors,
	},
	{
		id: "ads",
		label: "Ads",
		segment: "ads",
		section: "plan",
		icon: MegaphoneIcon,
		shortcut: "g d",
		page: pages.ads,
	},
	{
		id: "agents",
		label: "Agents",
		segment: "agents",
		section: "automate",
		icon: BotIcon,
		shortcut: "g a",
		page: pages.agents,
	},
	{
		id: "flows",
		label: "Flows",
		segment: "flows",
		section: "automate",
		icon: WorkflowIcon,
		shortcut: "g f",
		page: pages.flows,
	},
	{
		id: "artifacts",
		label: "Artifacts",
		segment: "artifacts",
		section: "automate",
		icon: ImagesIcon,
		shortcut: "g r",
		page: pages.artifacts,
	},
	{
		id: "assistant",
		label: "Assistant",
		segment: "assistant",
		icon: SparklesIcon,
		shortcut: "g j",
		page: pages.assistant,
		hidden: true,
	},
];

export const projectSettingsNavItem: ProjectNavItem = {
	id: "project-settings",
	label: "Project settings",
	segment: "settings",
	icon: SlidersHorizontalIcon,
	shortcut: "g ;",
	page: pages.projectSettings,
};

export const workspaceNavigation: WorkspaceNavItem[] = [
	{
		id: "projects",
		label: "All projects",
		path: "/projects",
		icon: FolderKanbanIcon,
		shortcut: "g o",
		page: pages.projects,
	},
	{
		id: "settings",
		label: "Settings",
		path: "/settings",
		icon: SettingsIcon,
		shortcut: "g ,",
		page: pages.accountSettings,
	},
	{
		id: "billing",
		label: "Billing & credits",
		path: "/settings/billing",
		icon: CreditCardIcon,
		shortcut: "g b",
		page: pages.billing,
	},
];
